import type { ApiResponse } from '@freelancer-os/shared-types';
import { useAuthStore } from '../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Token refresh queue to prevent concurrent refresh calls
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}> = [];

function processRefreshQueue(error: Error | null, token: string | null) {
  for (const { resolve, reject } of refreshQueue) {
    if (error) reject(error);
    else resolve(token!);
  }
  refreshQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, updateTokens, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token');
  }

  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const json = await res.json();
  if (!json.success) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const tokens = json.data as { accessToken: string; refreshToken: string };
  updateTokens(tokens);
  return tokens.accessToken;
}

async function getValidToken(): Promise<string> {
  const { accessToken } = useAuthStore.getState();
  return accessToken || '';
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getValidToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint}`;
  let response = await fetch(url, { ...options, headers });

  // Handle 401 — attempt token refresh
  if (response.status === 401 && token) {
    if (isRefreshing) {
      // Wait for the in-flight refresh to complete
      const newToken = await new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } else {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        processRefreshQueue(null, newToken);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } catch (err) {
        processRefreshQueue(err as Error, null);
        throw err;
      } finally {
        isRefreshing = false;
      }
    }
  }

  const text = await response.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    const error = new Error(json.error?.message || 'API Error') as Error & {
      code?: string;
      status?: number;
    };
    error.code = json.error?.code;
    error.status = response.status;
    throw error;
  }

  return (json as { success: true; data: T }).data;
}

/** Fetch without auth — for public endpoints (login, register, etc.) */
export async function fetchPublicApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const text = await response.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    const error = new Error(json.error?.message || 'API Error') as Error & {
      code?: string;
      status?: number;
    };
    error.code = json.error?.code;
    error.status = response.status;
    throw error;
  }

  return (json as { success: true; data: T }).data;
}

/**
 * Like fetchApi but returns the raw Response — for binary downloads (xlsx,
 * PDF, etc.) where we don't want JSON auto-parsing. Same auth + refresh
 * behavior as fetchApi.
 */
export async function fetchApiBlob(
  endpoint: string,
  options?: RequestInit,
): Promise<Response> {
  const token = await getValidToken();

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint}`;
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401 && token) {
    if (isRefreshing) {
      const newToken = await new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } else {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        processRefreshQueue(null, newToken);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } catch (err) {
        processRefreshQueue(err as Error, null);
        throw err;
      } finally {
        isRefreshing = false;
      }
    }
  }

  return response;
}
